import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { connectSocket, getSocket, onSocketEvent } from '../lib/socket';
import { AppNotification } from '../types/notification';

interface NotificationContextValue {
  notifications: AppNotification[];
  unreadCount: number;
  loading: boolean;
  fetchNotifications: () => Promise<void>;
  markAsRead: (id: string) => Promise<void>;
  markAllAsRead: () => Promise<void>;
  deleteNotification: (id: string) => Promise<void>;
}

const NotificationContext = createContext<NotificationContextValue | null>(null);

export const useNotifications = () => {
  const ctx = useContext(NotificationContext);
  if (!ctx) throw new Error('useNotifications must be used within NotificationProvider');
  return ctx;
};

export function NotificationProvider({ children }: { children: React.ReactNode }) {
  const [notifications, setNotifications] = useState<AppNotification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const socketCleanups = useRef<(() => void)[]>([]);

  const getAuthHeaders = () => {
    const token = localStorage.getItem('token');
    return { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` };
  };

  const fetchNotifications = useCallback(async () => {
    try {
      setLoading(true);
      const [notifRes, countRes] = await Promise.all([
        fetch('/api/notifications?limit=30', { headers: getAuthHeaders() }),
        fetch('/api/notifications/unread-count', { headers: getAuthHeaders() }),
      ]);

      if (notifRes.ok) {
        const { data } = await notifRes.json();
        setNotifications(data ?? []);
      }
      if (countRes.ok) {
        const { data } = await countRes.json();
        setUnreadCount(data?.count ?? 0);
      }
    } catch (err) {
      console.error('[Notifications] Fetch error:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  const markAsRead = useCallback(async (id: string) => {
    try {
      const res = await fetch(`/api/notifications/${id}/read`, {
        method: 'PATCH',
        headers: getAuthHeaders(),
      });
      if (res.ok) {
        setNotifications(prev => prev.map(n => n.id === id ? { ...n, read_at: new Date().toISOString() } : n));
        setUnreadCount(prev => Math.max(0, prev - 1));
      }
    } catch (err) {
      console.error('[Notifications] Mark as read error:', err);
    }
  }, []);

  const markAllAsRead = useCallback(async () => {
    try {
      const res = await fetch('/api/notifications/mark-all-read', {
        method: 'PATCH',
        headers: getAuthHeaders(),
      });
      if (res.ok) {
        setNotifications(prev => prev.map(n => ({ ...n, read_at: n.read_at ?? new Date().toISOString() })));
        setUnreadCount(0);
      }
    } catch (err) {
      console.error('[Notifications] Mark all read error:', err);
    }
  }, []);

  const deleteNotification = useCallback(async (id: string) => {
    try {
      const res = await fetch(`/api/notifications/${id}`, {
        method: 'DELETE',
        headers: getAuthHeaders(),
      });
      if (res.ok) {
        setNotifications(prev => {
          const removed = prev.find(n => n.id === id);
          if (removed && !removed.read_at) setUnreadCount(c => Math.max(0, c - 1));
          return prev.filter(n => n.id !== id);
        });
      }
    } catch (err) {
      console.error('[Notifications] Delete error:', err);
    }
  }, []);

  // Socket.io real-time listeners
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) return;

    connectSocket(token);

    const cleanupNew = onSocketEvent<AppNotification>('notification:new', (notif) => {
      setNotifications(prev => [notif, ...prev]);
      setUnreadCount(prev => prev + 1);
      toast.info(notif.title, { description: notif.message, duration: 5000 });
    });

    const cleanupAllRead = onSocketEvent<Record<string, never>>('notification:all-read', () => {
      setNotifications(prev => prev.map(n => ({ ...n, read_at: n.read_at ?? new Date().toISOString() })));
      setUnreadCount(0);
    });

    socketCleanups.current = [cleanupNew, cleanupAllRead];

    return () => {
      socketCleanups.current.forEach(fn => fn());
      socketCleanups.current = [];
    };
  }, []);

  // Initial fetch
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) fetchNotifications();
  }, [fetchNotifications]);

  return (
    <NotificationContext.Provider
      value={{ notifications, unreadCount, loading, fetchNotifications, markAsRead, markAllAsRead, deleteNotification }}
    >
      {children}
    </NotificationContext.Provider>
  );
}
